import React, { useMemo, useState, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Commodities } from "@/lib/api/commodity";
import { useFullRiskUnknown, ClientOpenPosition } from "@/lib/api/risk/summary";
import { useListRiskCashSales, RiskCashSale, RowFlagStatus, useActionCashSaleRows, ActionCashSales, RowFlagRule } from "@/lib/api/risk/cashSales/general";
import { get_customer_commodity_top_level, get_commodity_color, get_commodity_border_color, formatPercent, groupable_display_name_to_commodity_list, groupable_display_name_to_drillable_commodity_list, generateDisplayLabelForOtcDetailedType } from "./common";
import { ResponsivePie } from "@nivo/pie";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useQueryParam } from "@/hooks/use-query-param";
import { createColumnHelper, VisibilityState, ColumnDef, RowSelectionState } from "@tanstack/react-table";
import { formatNumberToDisplay } from "@/lib/api/common";
import { DataTable } from "@/components/ui/virtualizedTable";
import { ContractTypeOptions, TradingContract } from "@/lib/api/otc";
import { getPath } from "@/lib/prelude";
import { ColumnVisibilityDialog } from "@/components/ui/ColumnVisibilityDialog";
import { Button } from "@/components/ui/button";
import { PreviewDisplayCard } from "./PreviewDisplayCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getBrokerageTableColumns } from "./brokerageTableColumns";
import { getGeneralDetailColumns } from "./generalDetailColumns";
import { GenerateLoadingOrError } from "@/lib/utils";
import moment from "moment";
import { Badge } from "@/components/ui/badge";
import { ComboboxMulti } from "@/components/ui/combobox_mutli";
import { SelectOptionItem } from "@/lib/models/ui";
import { Loader, Plus, Edit, Trash2, MergeIcon, TriangleAlertIcon } from "lucide-react";
import { CropYearSelect } from "./CropYearSelect";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { useQueryParamArray } from '@/hooks/use-query-param-array';
import { cn } from "@/lib/utils";
import { UpdateCashSalesDialog } from "./UpdateCashSalesDialog";
import { UploadCashReportDialog } from '../UploadCashReportDialog';
import { Separator } from "@/components/ui/separator";

// Component to display cash sales that have flags

// Note these are all ids that correspond to something in UnknownData
interface UnknownSummary {
    applicable_positions: {
        no_crop_year: string[];
        unknown_contract: string[];
        unknown_customer: string[];
    };
    non_applicable_positions: {
        no_crop_year: string[];
        unknown_contract: string[];
        unknown_customer: string[];
    };
    cash_sale_no_crop_year: string[];
    customer_name_map: Record<string, string>;
}

interface UnknownData {
    // id to cash sale map
    contract_map: Record<string, RiskCashSale>;
    // id to otc contract
    detail_otc_contract_map: Record<string, TradingContract>;
    // Contract number to id map
    detail_otc_contract_number_map: Record<string, string>;
    // id to client position map
    position_map: Record<string, ClientOpenPosition>;
}

interface UnknownResult {
    summary: UnknownSummary;
    data: UnknownData;
}

function renderDetailTable(selectedCard: string, summary: UnknownSummary, data: UnknownData) {
    if (!selectedCard || !summary || !data) return null;
    let columnVisibility = undefined;

    // Get the IDs for the selected category
    let ids: string[] = [];
    let isPosition = false;
    
    switch(selectedCard) {
        case "applicable_no_crop_year":
            ids = summary.applicable_positions?.no_crop_year || [];
            isPosition = true;
            columnVisibility = { crop_year: false };
            break;
        case "applicable_unknown_contract":
            ids = summary.applicable_positions?.unknown_contract || [];
            isPosition = true;
            columnVisibility = { commodity: false };
            break;
        case "applicable_unknown_customer":
            ids = summary.applicable_positions?.unknown_customer || [];
            isPosition = true;
            columnVisibility = { customer_id: false };
            break;
        case "non_applicable_no_crop_year":
            ids = summary.non_applicable_positions?.no_crop_year || [];
            isPosition = true;
            columnVisibility = { crop_year: false };
            break;
        case "non_applicable_unknown_contract":
            ids = summary.non_applicable_positions?.unknown_contract || [];
            isPosition = true;
            columnVisibility = { commodity: false };
            break;
        case "non_applicable_unknown_customer":
            ids = summary.non_applicable_positions?.unknown_customer || [];
            isPosition = true;
            columnVisibility = { customer_id: false };
            break;
        case "cash_sale_no_crop_year":
            ids = summary.cash_sale_no_crop_year || [];
            columnVisibility = { crop_year: false };
            break;
    }

    if (isPosition) {
        // Get position data for the IDs
        const positions = ids.map(id => data.position_map?.[id]).filter(Boolean);
        const columns = getBrokerageTableColumns("", summary.customer_name_map);
        
        return (
            <DataTable
                columnVisibility={columnVisibility}
                height="600px"
                columns={columns as any}
                data={positions}
                className="mx-4"
            />
        );
    } else {
        // Cash sales table for "no crop year" contracts
        const contracts = ids.map(id => data.contract_map?.[id]).filter(Boolean);
        const lookupOtcType = (contractNumber?: string) => {
            const contract_id = contractNumber ? data.detail_otc_contract_number_map?.[contractNumber] : undefined;
            const contract = contract_id ? data.detail_otc_contract_map?.[contract_id] : undefined;
            return ContractTypeOptions.find((option) => option.value === contract?.contract_type)?.label || "";
        };
        const columns = getGeneralDetailColumns({
            commodityDisplayName: "", contractTypeLookupByNumber: lookupOtcType, customerNameMap: summary.customer_name_map
        });
        
        return (
            <DataTable
                columnVisibility={columnVisibility}
                height="600px"
                columns={columns as any}
                data={contracts}
                className="mx-4"
            />
        );
    }
}

export function FullUnknownSection() {
    const [customerIds, _customerIds] = useQueryParamArray("customer_ids");
    const [selectedCard, setSelectedCard] = useQueryParam("unknown_card");
    const fullRiskUnknownResult = useFullRiskUnknown({
        filters: {
            customer_ids: customerIds,
        }
    });

    // NOTE: tried to break down to risk vs not risk but it is hard when customer can be null
    const { summary, data }: UnknownResult = fullRiskUnknownResult?.data?.data || { summary: {}, data: {} } as any;

    if (fullRiskUnknownResult.isLoading) {
        return <Skeleton className="w-full h-96" />;
    }

    const loadingOrErrorUi = GenerateLoadingOrError([fullRiskUnknownResult]);
    if (loadingOrErrorUi) {
        return loadingOrErrorUi || null;
    }

    return (
        <>
            <BreakdownSection summary={summary} data={data} selectedCard={selectedCard} setSelectedCard={setSelectedCard} title="Risk" />
            <Separator />
        </>
    );
}

function BreakdownSection({title, summary, data, selectedCard, setSelectedCard }: { title: string; summary?: UnknownSummary; data?: UnknownData; selectedCard?: string; setSelectedCard: any }) {
    const unknownCategories = useMemo(() => {
        if (!summary) return [];

        return [
            {
                title: "Applicable Position - No Crop Year",
                count: summary.applicable_positions?.no_crop_year?.length || 0,
                key: "applicable_no_crop_year",
                color: "bg-blue-50 border-blue-200 hover:bg-blue-100",
                iconColor: "text-blue-600",
                description: "Positions missing crop year data"
            },
            {
                title: "Applicable Position - Unmapped Commodity",
                count: summary.applicable_positions?.unknown_contract?.length || 0,
                key: "applicable_unknown_contract",
                color: "bg-purple-50 border-purple-200 hover:bg-purple-100",
                iconColor: "text-purple-600",
                description: "Positions with unmapped commodities"
            },
            {
                title: "Applicable Position - Unknown Customer",
                count: summary.applicable_positions?.unknown_customer?.length || 0,
                key: "applicable_unknown_customer",
                color: "bg-indigo-50 border-indigo-200 hover:bg-indigo-100",
                iconColor: "text-indigo-600",
                description: "Positions with unidentified customers"
            },
            {
                title: "Non-Applicable Position - No Crop Year",
                count: summary.non_applicable_positions?.no_crop_year?.length || 0,
                key: "non_applicable_no_crop_year",
                color: "bg-orange-50 border-orange-200 hover:bg-orange-100",
                iconColor: "text-orange-600",
                description: "Non-applicable positions missing crop year"
            },
            {
                title: "Non-Applicable Position - Unmapped Commodity",
                count: summary.non_applicable_positions?.unknown_contract?.length || 0,
                key: "non_applicable_unknown_contract",
                color: "bg-red-50 border-red-200 hover:bg-red-100",
                iconColor: "text-red-600",
                description: "Non-applicable positions with unmapped commodities"
            },
            {
                title: "Non-Applicable Position - Unknown Customer",
                count: summary.non_applicable_positions?.unknown_customer?.length || 0,
                key: "non_applicable_unknown_customer",
                color: "bg-pink-50 border-pink-200 hover:bg-pink-100",
                iconColor: "text-pink-600",
                description: "Non-applicable positions with unidentified customers"
            },
            {
                title: "Cash Sale - No Crop Year",
                count: summary.cash_sale_no_crop_year?.length || 0,
                key: "cash_sale_no_crop_year",
                color: "bg-green-50 border-green-200 hover:bg-green-100",
                iconColor: "text-green-600",
                description: "Cash sales missing crop year information"
            }
        ];
    }, [summary]);

    if (!summary || !data) {
        return null;
    }

    const positionCategories = unknownCategories.filter(cat =>
        cat.key.includes('applicable_') && !cat.key.includes('cash_sale')
    );
    const applicablePositions = positionCategories.filter(cat =>
        cat.key.startsWith('applicable_')
    );
    const nonApplicablePositions = positionCategories.filter(cat =>
        cat.key.startsWith('non_applicable_')
    );
    const contractCategories = unknownCategories.filter(cat =>
        cat.key.includes('cash_sale')
    );

    return (
                <div className="flex flex-col gap-6">
            {!selectedCard ? (
                <>
                    <div className="mb-4">
                        <h2 className="text-2xl font-bold text-gray-900">Unknown Data Summary</h2>
                        <p className="text-gray-600 mt-1 text-sm">Click on any card to view detailed information. Keep in mind counts maybe shared across categories.</p>
                        <p className="text-gray-600 mt-1 text-sm">This applies light filtering around unknowns to not overwhelm users with non-relevant data. Such as filtering out DP contracts with a quantity of 0.</p>
                    </div>

                    {/* Contracts Section */}
                    <div className="border rounded-lg p-6 bg-gray-50">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Contracts</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {contractCategories.map((category) => (
                                <Card
                                    key={category.key}
                                    className={cn(
                                        "cursor-pointer transition-all duration-200 border-2",
                                        category.color
                                    )}
                                    onClick={() => setSelectedCard(category.key)}
                                >
                                    <CardHeader className="pb-3">
                                        <div className="flex items-start justify-between">
                                            <TriangleAlertIcon className={cn("h-5 w-5", category.iconColor)} />
                                            <Badge variant="secondary" className="ml-2">
                                                {formatNumberToDisplay(category.count)}
                                            </Badge>
                                        </div>
                                        <CardTitle className="text-base font-semibold mt-2">
                                            {category.title}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-sm text-gray-600">{category.description}</p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>

                    {/* Positions Section */}
                    <div className="space-y-6">
                        <div className="border rounded-lg p-6 bg-gray-50">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Positions</h3>

                            {/* Applicable Positions */}
                            <div className="mb-6">
                                <h4 className="text-sm font-medium text-gray-700 mb-3">Applicable Positions</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {applicablePositions.map((category) => (
                                        <Card
                                            key={category.key}
                                            className={cn(
                                                "cursor-pointer transition-all duration-200 border-2",
                                                category.color
                                            )}
                                            onClick={() => setSelectedCard(category.key)}
                                        >
                                            <CardHeader className="pb-3">
                                                <div className="flex items-start justify-between">
                                                    <TriangleAlertIcon className={cn("h-5 w-5", category.iconColor)} />
                                                    <Badge variant="secondary" className="ml-2">
                                                        {formatNumberToDisplay(category.count)}
                                                    </Badge>
                                                </div>
                                                <CardTitle className="text-base font-semibold mt-2">
                                                    {category.title.replace('Applicable Position - ', '')}
                                                </CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <p className="text-sm text-gray-600">{category.description}</p>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            </div>

                            {/* Non-Applicable Positions */}
                            <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-3">Non-Applicable Positions</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {nonApplicablePositions.map((category) => (
                                        <Card
                                            key={category.key}
                                            className={cn(
                                                "cursor-pointer transition-all duration-200 border-2",
                                                category.color
                                            )}
                                            onClick={() => setSelectedCard(category.key)}
                                        >
                                            <CardHeader className="pb-3">
                                                <div className="flex items-start justify-between">
                                                    <TriangleAlertIcon className={cn("h-5 w-5", category.iconColor)} />
                                                    <Badge variant="secondary" className="ml-2">
                                                        {formatNumberToDisplay(category.count)}
                                                    </Badge>
                                                </div>
                                                <CardTitle className="text-base font-semibold mt-2">
                                                    {category.title.replace('Non-Applicable Position - ', '')}
                                                </CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <p className="text-sm text-gray-600">{category.description}</p>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-xl font-semibold">
                            {unknownCategories.find(c => c.key === selectedCard)?.title}
                        </h3>
                    </div>

                    <Card>
                        <CardContent className="pt-6">
                            {renderDetailTable(selectedCard, summary, data)}
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    )

}
